export type GooglePassThrough = {
  id: string;
  verified_email: boolean;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  hd: string;
  email: string;
};

export type ClasslinkPassThrough = {
  iss: string;
  sub: string;
  aud: string;
  nonce: string;
  exp: number;
  iat: number;
  auth_time: number;
  login_hint: string;
  email_verified: boolean;
  email: string;
  name: string;
  profile: string;
  address: {
    locality: string;
    region: string;
  };
  given_name: string;
  family_name: string;
  classLink_tenant_id: number;
  classLink_role: string;
  classLink_role_level: number;
  rfp: string;
  access_token: string;
};

export type MicrosoftPassThrough = {
  access_token: string;
  oid: string;
  name: string;
  scp: string;
  sub: string;
  picture: string;
  upn: string;
  unique_name: string;
};

export type CleverPassThrough = {
  id: string;
  type: string;
  authorized_by: string;
  email: string;
  name: { first: string; last: string };
  roles: { teacher: { legacy_id: string } };
  access_token: string;
};

/**
 * Raw data being returned from the provider
 */
type RosterstreamPassThroughData = GooglePassThrough | ClasslinkPassThrough | MicrosoftPassThrough | CleverPassThrough;

/**
 * Supported providers type for Rosterstream
 */
export type RosterstreamProviderType = 'google' | 'classlink' | 'microsoft' | 'clever';

export type RosterstreamRawUser = {
  rsId: string;
  role: string;
  dsId: string;
  data: {
    sourcedId: string;
    status: string;
    dateLastModified: string;
    username: string;
    enabledUser: string;
    givenName: string;
    familyName: string;
    role: string;
    identifier: string;
    // identifier we use to match in minga
    email: string;
    orgs: Array<{
      href: string;
      // this should match School.sourcedId
      sourcedId: string;
      type: string;
    }>;
  };
};

/**
 * user = user was returned from rosterstream users
 * passthrough (fallback) = email was returned from passthrough data provided by the SSO provider
 */
export type UserSource = 'user' | 'passthrough';

export type RosterstreamUser = {
  provider: RosterstreamProviderType;
  email: string;
  userSource: UserSource;
};

export type RosterstreamUserRawData = {
  passthrough: RosterstreamPassThroughData;
  users: RosterstreamRawUser[];
  addId: string;
  dsrcId: string;
  provider: RosterstreamProviderType;
};

/**
 * Response data from the Rosterstream verify API
 */
export type RosterstreamVerifyApiResponse = RosterstreamUserRawData | { error: RosterstreamError };

export enum RosterstreamError {
  // the provider is not enabled, please check the toggle on the right side next to the provider name is enabled.
  PROVIDER_NOT_ENABLED = 'PROVIDER_NOT_ENABLED',
  // the app id is required in the url path for lookup of the config, please copy the URL from the "Login" links for the given provider.
  APP_ID_REQUIRED = 'APP_ID_REQUIRED',
  // there is no config found for the specified app, please check the sso tab for the app id that was used in the url.
  NO_CONFIG_FOR_APP = 'NO_CONFIG_FOR_APP',
  // A callback url is required in the config for the app
  NO_VALID_CALLBACKS = 'NO_VALID_CALLBACKS',
  // if you are passing along a specific callback as a url search parameter, said url must appear in the app config (as a single entry, or as a comma separated list for multiple).
  INVALID_CALLBACK_URL = 'INVALID_CALLBACK_URL',
  // incomplete config for the provider
  PROVIDER_MISSING_CONFIGURATION = 'PROVIDER_MISSING_CONFIGURATION',
  // at the SSO provider level there was no user found. If this is Microsoft SSO (Entra Id) the user may not have a valid upn or unique_name. while the unique_name field is noted as "not unique" it's a backup for upn which is the only noted unique field by Microsoft and should theoretically be the email.
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  // for LTI and SAML the datasource id is required in the path for lookup of the config of the specific datasource. Please copy the URL from the "Login" links for the given provider.
  DATASOURCE_ID_REQUIRED = 'DATASOURCE_ID_REQUIRED',
  // for LTI and SAML there is a config specific to each datasource (district), this is thrown when there is no config for said datasource.
  NO_CONFIG_FOR_DATASOURCE = 'NO_CONFIG_FOR_DATASOURCE',
  // for LTI setup a secret must be generated and used for the LTI provider configuration signing
  SECRET_REQUIRED = 'SECRET_REQUIRED',
  KEY_DSRCID_MISMATCH = 'KEY_DSRCID_MISMATCH',
  // The signature doesn't match for the LTI launch, likely you don't have the correct certificate or aren't using Canvas and the payload for signing is different. If this is the case let us know!
  INVALID_OAUTH_SIGNATURE = 'INVALID_OAUTH_SIGNATURE',
  // if you are seeing this message the asset endpoint is currently only setup for POST, and login is only setup for GET requests. if you reach this message and need additional methods let us know.
  INVALID_SAML_ACTION = 'INVALID_SAML_ACTION',
  // invalid saml signature, make sure the certificate is the correct one from the IDP provider for the given datasource, otherwise the assertion might have been tampered with.
  SAML_SIGNATURE_INVALID = 'SAML_SIGNATURE_INVALID',
  // Potential replay attack, or there is a significant delay in hand off of the saml assertion to our SSO service
  SAML_ASSERTION_EXPIRED = 'SAML_ASSERTION_EXPIRED',
  // The user trying to login isn't configured for access
  SAML_INVALID_AUDIENCE = 'SAML_INVALID_AUDIENCE',
  // for simple authentication, a callback url is required in the config
  EMPTY_CALLBACK = 'EMPTY_CALLBACK',
  // there was no code provided to the callback endpoint, someone is probing the endpoint or canceled out of the auth flow
  CODE_REQUIRED = 'CODE_REQUIRED',

  // ===== Minga specific errors ====
  // couldn't match a user in our system from Rosterstream user data
  MINGA_USER_NOT_FOUND = 'MINGA_USER_NOT_FOUND',
  // the state parameter is invalid
  INVALID_STATE = 'INVALID_STATE',
  // unknown error (catch all)
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}
